import React, {useEffect, useState} from 'react';
import {Button, Stack, Typography} from '@mui/material';
import {InterfaceEntity, InterfaceEntityTypeEnum} from '@store/settings/interface/interface.model';
import SelectComponent from '@components/inputs/select/Select.component';
import {useTranslation} from 'react-i18next';
import NewTableComponent from '@components/table/NewTable.component';
import {useObservable} from '@ngneat/react-rxjs';
import {interfaceQuery, interfaceService} from '@store/settings/interface';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import EditInterfaceEntityModal from '@screens/admin/settings/interface/EditInterfaceEntity.modal';
import {Add} from '@icons/index';
import SearchComponent from '@components/inputs/search/Search.component';
import {emitOnce} from '@ngneat/elf';

const InterfaceTab = () => {

  const {t} = useTranslation();

  const [tablePagination] = useObservable(interfaceQuery.pagination$);
  const [filters] = useObservable(interfaceQuery.filters$);

  const [{interfaceEntities, loading}] = useObservable(interfaceQuery.interfaceEntities$);

  const [selectedEntity, setSelectedEntity] = useState<InterfaceEntity | undefined>(undefined);

  useEffect(() => {
    interfaceService.getInterfaceEntities().subscribe();
  }, [filters, tablePagination]);

  return (
    <>
      <Stack flex={1} width="100%" spacing={2} p="25px">
        <Typography fontSize="30px" fontWeight="700">
          {t('routes.interface')}
        </Typography>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center">
            <SelectComponent
              inputWidth={300}
              color="secondary"
              items={InterfaceEntityTypeEnum.selectItems()}
              value={filters.entityType}
              handleChange={interfaceService.updateFilters('entityType')}
              label={t('settings.interfaces.entityTypeSelect')}
            />
            <SearchComponent
              value={filters.search}
              handleChange={(value) => {
                emitOnce(() => {
                  interfaceService.setFilters('search')(value);
                  interfaceService.setPagination('page')(1);
                });
              }}
            />
          </Stack>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setSelectedEntity({id: -1})}
            startIcon={<Add/>}
          >
            {t('settings.interfaces.addNew')}
          </Button>
        </Stack>
        <NewTableComponent
          loading={loading}
          columns={[
            COLUMN_TYPES.TEXT({
              field: 'id',
              label: t('settings.interfaces.table.id'),
            }),
            COLUMN_TYPES.TEXT({
              field: 'metawasteTableName',
              width: 200,
              label: t('settings.interfaces.table.metawasteTableName'),
            }),
            COLUMN_TYPES.TEXT({
              field: 'name',
              width: 350,
              label: t('settings.interfaces.table.name'),
            }),
            COLUMN_TYPES.TEXT({
              field: 'datahubId',
              label: t('settings.interfaces.table.datahubId'),
            }),
            COLUMN_TYPES.TEXT({
              field: 'datahubTableName',
              label: t('settings.interfaces.table.datahubTableName'),
            }),
            COLUMN_TYPES.TEXT({
              field: 'datahubOrigin',
              label: t('settings.interfaces.table.datahubOrigin'),
            }),
            COLUMN_TYPES.ACTIONS({
              handleShowRow: (e: InterfaceEntity) =>
                setSelectedEntity(e),
            }),
          ]}
          handlePageSizeChange={(value) => {
            interfaceService.setPagination('limit')(value);
            interfaceService.setPagination('page')(1);
          }}
          pageSize={tablePagination.limit}
          handlePageChange={(page) => interfaceService.setPagination('page')(page + 1)}
          page={tablePagination.page - 1}
          data={interfaceEntities}
          totalRows={interfaceQuery.totalRows}
          disableRowSelection
        />
      </Stack>
      {selectedEntity && <EditInterfaceEntityModal entityType={filters.entityType} entity={selectedEntity}
                                                   handleClose={() => setSelectedEntity(undefined)}/>}
    </>
  );
};

export default InterfaceTab;
